import type { Field as BaseUIField } from '@base-ui/react/field'
import type { ComponentProps, ReactElement, ReactNode, Ref } from 'react'

/**
 * Field size variants - controls gap and text sizing
 */
export const FieldSize = {
  Small: 'small',
  Medium: 'medium',
  Large: 'large',
} as const

export type FieldSize = (typeof FieldSize)[keyof typeof FieldSize]

/**
 * Where the label sits relative to its control.
 *
 * - `vertical` (default): the label above the control.
 * - `horizontal`: a two-column row, label on the left and the control in the
 *   right half; a description or error sits under the control.
 */
export const FieldOrientation = {
  Vertical: 'vertical',
  Horizontal: 'horizontal',
} as const

export type FieldOrientation =
  (typeof FieldOrientation)[keyof typeof FieldOrientation]

/**
 * Props for the FieldRoot component
 */
export interface FieldRootProps
  extends Omit<
    ComponentProps<typeof BaseUIField.Root>,
    'className' | 'children'
  > {
  className?: string
  children: ReactNode
  /**
   * Controls gap, label text size, and propagates to child controls (Input, Select.Input, etc.)
   * via context. Do not set `size` directly on child controls inside a Field - the Field size
   * should be the single source of truth. A `size` set on a child control will override the
   * Field value, causing visual inconsistency.
   */
  size?: FieldSize
  /** Label above the control (default) or beside it. */
  orientation?: FieldOrientation
  ref?: Ref<HTMLDivElement>
}

/**
 * Props for the FieldLabel component
 */
export interface FieldLabelProps
  extends Omit<
    ComponentProps<typeof BaseUIField.Label>,
    'className' | 'children'
  > {
  className?: string
  children: ReactNode
  required?: boolean
  /**
   * Rendered at the far right of the label row, as a sibling of the `<label>`
   * (a button inside a label is invalid and would activate the control).
   * Pair an `InlineTrigger` + `Text` for a link-style action with no extra height.
   */
  action?: ReactNode
  ref?: Ref<HTMLLabelElement>
}

/**
 * Props for the FieldDescription component
 */
export interface FieldDescriptionProps
  extends Omit<
    ComponentProps<typeof BaseUIField.Description>,
    'className' | 'children' | 'ref'
  > {
  className?: string
  children: ReactNode
  ref?: Ref<HTMLParagraphElement>
}

/**
 * Props for the FieldError component.
 *
 * The `match` prop controls conditional rendering and ARIA linking:
 * - `match={true}` (default) — always renders, links via `aria-describedby`
 * - `match={false}` — does not render, no `aria-describedby` link
 * - `match={condition}` — renders and links only when condition is truthy
 *
 * Parent-controlled conditional rendering (`{hasError && <Field.Error>...}`)
 * still works but does not remove the `aria-describedby` link when unmounted.
 * Prefer the `match` prop for correct ARIA behavior.
 */
export interface FieldErrorProps
  extends Omit<
    ComponentProps<typeof BaseUIField.Error>,
    'className' | 'children'
  > {
  className?: string
  children: ReactNode
}

/**
 * Props for the FieldControl component
 *
 * Wraps a form control to automatically receive disabled/invalid state
 * from the parent Field, and links ARIA attributes to Label/Description.
 */
export interface FieldControlProps
  extends Omit<
    ComponentProps<typeof BaseUIField.Control>,
    'className' | 'render'
  > {
  className?: string
  children: ReactElement
}

/**
 * Props for the FieldItem component
 *
 * Groups a control (checkbox/radio) with its label within a Field.
 * Useful for radio groups or checkbox lists where each item needs individual labels.
 */
export interface FieldItemProps
  extends Omit<ComponentProps<typeof BaseUIField.Item>, 'className'> {
  className?: string
  children: ReactNode
  /**
   * Adds row padding and a hover background so the full-row hit area is visible.
   * Use for stacked option lists (settings, radio/checkbox groups); leave off for
   * inline form rows where a highlight would read as accidental. Horizontal padding
   * is offset with negative margin so content stays aligned with the container edge.
   */
  interactive?: boolean
}

/**
 * Props for the FieldContent component
 *
 * Column wrapper for a label + description paired with a leading control
 * (checkbox/radio). As the second flex child of Field.Item, it indents the
 * description under the label text without any padding math.
 */
export interface FieldContentProps {
  className?: string
  children: ReactNode
  ref?: Ref<HTMLDivElement>
}
